export const TTL = {
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
} as const

interface Envelope<T> {
  value: T
  expiresAt?: number
}

interface LocalCacheEnvelope {
  c: number
  e: number
  v: string
}

interface KeyOptions {
  ttl?: number
}

interface SetOptions {
  ttl?: number
}

const NEVER_EXPIRES = 253402300799000

function isLocalCacheEnvelope(value: unknown): value is LocalCacheEnvelope {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<LocalCacheEnvelope>
  return typeof candidate.c === 'number' && typeof candidate.e === 'number' && typeof candidate.v === 'string'
}

function isLegacyEnvelope<T>(value: unknown): value is Envelope<T> {
  if (!value || typeof value !== 'object') return false
  return 'value' in value
}

function webStorageKey<T>(storage: Storage, key: string, defaults: KeyOptions = {}) {
  return {
    get(): T | null {
      const raw = storage.getItem(key)
      if (!raw) return null
      try {
        const envelope = JSON.parse(raw) as unknown
        if (isLocalCacheEnvelope(envelope)) {
          if (Date.now() > envelope.e) {
            storage.removeItem(key)
            return null
          }
          return JSON.parse(envelope.v) as T
        }

        if (isLegacyEnvelope<T>(envelope)) {
          if (envelope.expiresAt !== undefined && Date.now() > envelope.expiresAt) {
            storage.removeItem(key)
            return null
          }
          return envelope.value
        }

        storage.removeItem(key)
        return null
      } catch {
        storage.removeItem(key)
        return null
      }
    },
    getExpiresAt(): number | null {
      const raw = storage.getItem(key)
      if (!raw) return null
      try {
        const envelope = JSON.parse(raw) as unknown
        const expiresAt = isLocalCacheEnvelope(envelope)
          ? envelope.e
          : isLegacyEnvelope<T>(envelope)
            ? envelope.expiresAt ?? NEVER_EXPIRES
            : null
        if (expiresAt === null || Date.now() > expiresAt) {
          storage.removeItem(key)
          return null
        }
        return expiresAt
      } catch {
        storage.removeItem(key)
        return null
      }
    },
    set(value: T, options?: SetOptions): void {
      const ttl = options?.ttl ?? defaults.ttl
      const now = Date.now()
      const envelope: LocalCacheEnvelope = {
        c: now,
        e: ttl === undefined ? NEVER_EXPIRES : now + ttl,
        v: JSON.stringify(value),
      }
      try {
        storage.setItem(key, JSON.stringify(envelope))
      } catch (e) {
        if (e instanceof DOMException && e.name === 'QuotaExceededError') return
        throw e
      }
    },
    remove(): void {
      storage.removeItem(key)
    },
  }
}

export function storageKey<T>(key: string, defaults: KeyOptions = {}) {
  return webStorageKey<T>(localStorage, key, defaults)
}

export function sessionStorageKey<T>(key: string, defaults: KeyOptions = {}) {
  return webStorageKey<T>(sessionStorage, key, defaults)
}
